import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../components/Button';
import { ReviewerModal } from '../components/ReviewerModal';
import { Spinner } from '../components/Spinner';
import { hitlService } from '../services/hitl';
import type { HitlItem, HitSeverity } from '../services/types';
import { ApiError } from '../services/types';
import { useAppStore } from '../store/appStore';

/**
 * Cross-run HITL reviewer workbench.
 *
 * Data flow
 * ---------
 * - Fetches `/hits` on mount and populates Zustand's `hitsIndex`.
 * - Refetches whenever `hitlVersion` bumps (a WS `hitl_created` landed).
 * - Filters + sort are local (no server round-trip per keystroke).
 * - Bulk actions fire in sequence; the visible row state flips optimistically
 *   via Zustand, so a row never looks unresolved after a successful accept.
 *
 * Keyboard
 * --------
 * - Row click opens the ReviewerModal; Enter on the focused row does the same.
 * - Escape inside the modal closes it. See ReviewerModal for the focus trap.
 */
export function ReviewerDashboard() {
  const hitsIndex = useAppStore((s) => s.hitsIndex);
  const upsertHits = useAppStore((s) => s.upsertHits);
  const hitlVersion = useAppStore((s) => s.hitlVersion);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openHitId, setOpenHitId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkPending, setBulkPending] = useState(false);

  // Filter + sort state.
  const [filterSeverity, setFilterSeverity] = useState<HitSeverity | 'all'>('all');
  const [filterAgent, setFilterAgent] = useState<string>('all');
  const [filterColumn, setFilterColumn] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<HitlItem['status'] | 'all'>('open');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await hitlService.list();
      upsertHits(items);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API ${err.status}: ${err.message}`
          : err instanceof Error
            ? err.message
            : 'Unknown error';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [upsertHits]);

  // Initial load + WS-triggered refresh.
  useEffect(() => {
    void refetch();
  }, [refetch, hitlVersion]);

  const allHits = useMemo(() => Object.values(hitsIndex), [hitsIndex]);

  const agents = useMemo(() => uniq(allHits.map((h) => h.agent_id)), [allHits]);
  const columns = useMemo(() => uniq(allHits.map((h) => h.column)), [allHits]);

  const filtered = useMemo(() => {
    let list = allHits;
    if (filterSeverity !== 'all') list = list.filter((h) => h.severity === filterSeverity);
    if (filterAgent !== 'all') list = list.filter((h) => h.agent_id === filterAgent);
    if (filterColumn !== 'all') list = list.filter((h) => h.column === filterColumn);
    if (filterStatus !== 'all') list = list.filter((h) => h.status === filterStatus);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (h) =>
          h.issue.toLowerCase().includes(q) ||
          h.column.toLowerCase().includes(q) ||
          h.agent_id.toLowerCase().includes(q) ||
          h.hit_id.toLowerCase().includes(q) ||
          h.run_id.toLowerCase().includes(q),
      );
    }
    list = [...list].sort((a, b) => compareBy(a, b, sortBy, sortDir));
    return list;
  }, [allHits, filterSeverity, filterAgent, filterColumn, filterStatus, search, sortBy, sortDir]);

  const openHits = useMemo(() => filtered.filter((h) => h.status === 'open'), [filtered]);

  const openHit = openHitId ? hitsIndex[openHitId] ?? null : null;

  const toggleSelected = (hitId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(hitId)) next.delete(hitId);
      else next.add(hitId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      // If any of the visible open hits is unchecked, select all visible open
      // hits; otherwise clear.
      const visibleOpen = openHits.map((h) => h.hit_id);
      const allSelected = visibleOpen.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(visibleOpen);
    });
  };

  const bulkAccept = async () => {
    if (selected.size === 0) return;
    setBulkPending(true);
    setError(null);
    const ids = Array.from(selected);
    try {
      // Sequential (not Promise.all) so audit log is ordered and the mock
      // doesn't see a thundering herd of writes.
      for (const id of ids) {
        await hitlService.act(id, { type: 'accept', reviewer_id: 'reviewer' });
      }
      await refetch();
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk action failed');
    } finally {
      setBulkPending(false);
    }
  };

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  };

  return (
    <section aria-labelledby="hitl-dashboard-title" className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 id="hitl-dashboard-title" className="mb-1">HITL reviewer workbench</h1>
          <p className="text-sm text-muted">
            Cross-run queue of human-in-the-loop items. Filter, triage, open a row to apply an
            option, or mark a batch resolved.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {loading && <Spinner className="h-4 w-4" />}
          <Button variant="secondary" onClick={() => void refetch()} disabled={loading}>
            Refresh
          </Button>
        </div>
      </header>

      <div className="grid gap-3 md:grid-cols-5 text-sm">
        <label className="space-y-1">
          <span className="text-xs uppercase tracking-wide text-muted">Severity</span>
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value as HitSeverity | 'all')}
            className="w-full rounded border border-border bg-paper px-2 py-1.5"
          >
            <option value="all">All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs uppercase tracking-wide text-muted">Agent</span>
          <select
            value={filterAgent}
            onChange={(e) => setFilterAgent(e.target.value)}
            className="w-full rounded border border-border bg-paper px-2 py-1.5"
          >
            <option value="all">All</option>
            {agents.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs uppercase tracking-wide text-muted">Column</span>
          <select
            value={filterColumn}
            onChange={(e) => setFilterColumn(e.target.value)}
            className="w-full rounded border border-border bg-paper px-2 py-1.5"
          >
            <option value="all">All</option>
            {columns.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs uppercase tracking-wide text-muted">Status</span>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as HitlItem['status'] | 'all')}
            className="w-full rounded border border-border bg-paper px-2 py-1.5"
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="escalated">Escalated</option>
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-xs uppercase tracking-wide text-muted">Search</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="issue, column, id…"
            className="w-full rounded border border-border bg-paper px-2 py-1.5"
          />
        </label>
      </div>

      {/* Bulk-action bar */}
      <div
        role="toolbar"
        aria-label="Bulk actions"
        className="flex items-center gap-3 border border-border rounded-md px-4 py-2 bg-surface-alt/40"
      >
        <span className="text-sm text-muted">
          {selected.size === 0
            ? `${filtered.length} item${filtered.length === 1 ? '' : 's'} · ${openHits.length} open`
            : `${selected.size} selected`}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={bulkAccept}
            disabled={selected.size === 0 || bulkPending}
          >
            {bulkPending ? 'Accepting…' : 'Accept selected'}
          </Button>
          <Button
            variant="ghost"
            onClick={() => setSelected(new Set())}
            disabled={selected.size === 0 || bulkPending}
          >
            Clear selection
          </Button>
        </div>
      </div>

      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}

      {/* Table */}
      {filtered.length === 0 && !loading ? (
        <p className="text-sm text-muted italic">No hits match the current filters.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-alt/60 text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    aria-label="Select all visible open hits"
                    checked={
                      openHits.length > 0 && openHits.every((h) => selected.has(h.hit_id))
                    }
                    onChange={toggleAll}
                    disabled={openHits.length === 0}
                  />
                </th>
                <SortHeader label="Severity" active={sortBy === 'severity'} dir={sortDir} onClick={() => toggleSort('severity')} />
                <SortHeader label="Column" active={sortBy === 'column'} dir={sortDir} onClick={() => toggleSort('column')} />
                <SortHeader label="Agent" active={sortBy === 'agent_id'} dir={sortDir} onClick={() => toggleSort('agent_id')} />
                <th className="px-3 py-2 text-left">Issue</th>
                <SortHeader label="Status" active={sortBy === 'status'} dir={sortDir} onClick={() => toggleSort('status')} />
                <th className="px-3 py-2 text-left">Run</th>
                <SortHeader label="Created" active={sortBy === 'created_at'} dir={sortDir} onClick={() => toggleSort('created_at')} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((h) => (
                <tr
                  key={h.hit_id}
                  className="border-t border-border hover:bg-surface-alt/40 cursor-pointer"
                  onClick={() => setOpenHitId(h.hit_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setOpenHitId(h.hit_id);
                  }}
                  tabIndex={0}
                >
                  <td
                    className="px-3 py-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      aria-label={`Select ${h.hit_id}`}
                      checked={selected.has(h.hit_id)}
                      onChange={() => toggleSelected(h.hit_id)}
                      disabled={h.status !== 'open'}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded-sm px-2 py-0.5 text-xs font-semibold uppercase ${severityStyles[h.severity]}`}
                    >
                      {h.severity}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{h.column}</td>
                  <td className="px-3 py-2 text-xs text-muted">{h.agent_id}</td>
                  <td className="px-3 py-2 max-w-md truncate" title={h.issue}>
                    {h.issue}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-xs ${statusStyles[h.status]}`}>{h.status}</span>
                  </td>
                  <td
                    className="px-3 py-2 font-mono text-xs"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Link
                      to={`/runs/${encodeURIComponent(h.run_id)}/hitl`}
                      className="text-brand-dark hover:underline"
                    >
                      {h.run_id}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted whitespace-nowrap">
                    {formatCreated(h.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ReviewerModal
        hit={openHit}
        isOpen={openHitId !== null && openHit !== null}
        onClose={() => setOpenHitId(null)}
        reviewerId="reviewer"
      />
    </section>
  );
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

type SortKey = 'severity' | 'column' | 'agent_id' | 'status' | 'created_at';

const severityOrder: Record<HitSeverity, number> = { high: 3, medium: 2, low: 1 };
const statusOrder: Record<HitlItem['status'], number> = { open: 3, escalated: 2, resolved: 1 };

function compareBy(a: HitlItem, b: HitlItem, key: SortKey, dir: 'asc' | 'desc'): number {
  const sign = dir === 'asc' ? 1 : -1;
  switch (key) {
    case 'severity':
      return (severityOrder[a.severity] - severityOrder[b.severity]) * sign;
    case 'status':
      return (statusOrder[a.status] - statusOrder[b.status]) * sign;
    case 'column':
      return a.column.localeCompare(b.column) * sign;
    case 'agent_id':
      return a.agent_id.localeCompare(b.agent_id) * sign;
    case 'created_at':
      return (a.created_at < b.created_at ? -1 : 1) * sign;
  }
}

function uniq(list: string[]): string[] {
  return Array.from(new Set(list)).sort();
}

function formatCreated(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

const severityStyles: Record<HitSeverity, string> = {
  low: 'bg-brand/15 text-brand-dark',
  medium: 'bg-warn/15 text-warn',
  high: 'bg-danger/15 text-danger',
};

const statusStyles: Record<HitlItem['status'], string> = {
  open: 'text-warn font-medium',
  resolved: 'text-brand-dark',
  escalated: 'text-danger',
};

function SortHeader({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: () => void;
}) {
  return (
    <th className="px-3 py-2 text-left">
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 text-xs uppercase tracking-wide ${active ? 'text-ink' : 'text-muted'}`}
      >
        {label}
        {active && <span aria-hidden>{dir === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}
